import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { ulid } from 'ulid';
import { catchError, map, switchMap } from 'rxjs/operators';
import { cleanTypename } from '../../segments/helpers/handleData.helper';
import { BehaviorSubject, forkJoin, Observable, of, tap, throwError } from 'rxjs';
import { CurrentUserService } from '@core/services/currentUser.service';
import { PermissionType } from '@admin/modules/users/models/user.model';

import { GET_CAMPAIGNS_LIST, GET_COLLECTOR } from '@modules/campaigns/graphql/campaigns.graphql';
import {
  ADD_WORKFLOW,
  DELETE_WORKFLOW,
  GET_LINKED_WORKFLOWS,
  GET_WORKFLOW_PRIORITY_BY_TENANT,
  GET_WORKFLOW_TRIGGERS,
  GET_WORKFLOWS,
  PAUSE_WORKFLOW,
  RESUME_WORKFLOW,
  UNPUBLISH_WORKFLOW,
  UPDATE_WORKFLOW,
  UPDATE_WORKFLOW_PRIORITY,
} from '@modules/campaigns/graphql/workflows.graphql';
import { SEARCH_CAMPAIGNS_COLLECTORS_LIST } from '@modules/campaigns/graphql/campaings-collectors.graphql';
import {
  CAMPAIGN_TYPE,
  CampaignCollector,
  CampaignCollectorWithDetail,
  CampaignNewModel,
} from '@modules/campaigns-new/Domain/campaing.model';

@Injectable({
  providedIn: 'root',
})
export class CampaignNewService {
  isLoading$ = new BehaviorSubject<boolean>(false);
  public campaignCollectorsMap = new Map<string, CampaignCollector[]>();

  constructor(
    private route: ActivatedRoute,
    private apollo: Apollo,
    private currentUser: CurrentUserService,
  ) {}

  getCampaignList(type: CAMPAIGN_TYPE | CAMPAIGN_TYPE[]): Observable<CampaignNewModel[]> {
    const types = Array.isArray(type) ? type : [type];
    return this.apollo
      .query({
        query: GET_WORKFLOWS,
        variables: {
          tenantId: this.route.snapshot.queryParams['tenant'],
          types: types,
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res: any) => {
          if (res.errors?.length) {
            return [];
          } else {
            return res.data.workflows;
          }
        }),
      );
  }

  getTriggers(): Observable<any> {
    return this.apollo
      .query({
        query: GET_WORKFLOW_TRIGGERS,
        variables: {
          tenantId: this.route.snapshot.queryParams['tenant'],
        },
      })
      .pipe(map((res: any) => res));
  }

  getLinkedWorkflows(id: string): Observable<any> {
    return this.apollo
      .query({
        query: GET_LINKED_WORKFLOWS,
        variables: { id },
      })
      .pipe(
        map((res: any) => {
          if (res && res.errors?.length) {
            throw new Error(res.errors[0].message);
          }
          console.log('RES LINKED', res.data.linkedWorkflows);
          return res.data.linkedWorkflows;
        }),
        catchError((error) => {
          throw new Error(error);
        }),
      );
  }

  resumeCampaign(campaign: any): Observable<any> {
    return this.apollo
      .mutate({
        mutation: RESUME_WORKFLOW,
        variables: {
          input: {
            id: campaign.id,
          },
        },
      })
      .pipe(map((res: any) => res));
  }

  pauseCampaign(campaign: any): Observable<any> {
    return this.apollo
      .mutate({
        mutation: PAUSE_WORKFLOW,
        variables: {
          input: {
            id: campaign.id,
          },
        },
      })
      .pipe(map((res: any) => res));
  }

  unPublishCampaign(campaign: any): Observable<any> {
    return this.apollo
      .mutate({
        mutation: UNPUBLISH_WORKFLOW,
        variables: {
          input: {
            id: campaign.id,
          },
        },
      })
      .pipe(map((res: any) => res));
  }
  isFullAccess() {
    return this.currentUser.isPermissionAuthorized([PermissionType.FULL_ACCESS]);
  }

  deleteDraftCampaign(campaign: CampaignNewModel): Observable<any> {
    return this.apollo
      .mutate({
        mutation: DELETE_WORKFLOW,
        variables: {
          id: campaign.id,
        },
      })
      .pipe(map((res: any) => res));
  }

  getCampaignListPriority(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .query({
          query: GET_WORKFLOW_PRIORITY_BY_TENANT,
          variables: {
            tenantId: this.route.snapshot.queryParams['tenant'],
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res.errors[0].message);
          } else {
            resolve(res.data.workflowPriorityByTenant.workflowIds);
          }
        });
    });
  }

  updateCampaignPriority(priority: any): Promise<void> {
    delete priority['__typename'];
    return new Promise<any>((resolve, reject) => {
      this.apollo
        .mutate({
          mutation: UPDATE_WORKFLOW_PRIORITY,
          variables: {
            input: {
              tenantId: this.route.snapshot.queryParams['tenant'],
              workflowIds: priority,
            },
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) {
            reject(res.errors[0].message);
          } else {
            resolve(res.data.updateWorkflow);
          }
        });
    });
  }

  cloneCampaign(campaign: any, _name?: string): Observable<any> {
    campaign = cleanTypename(campaign);
    const {
      name,
      purpose,
      type,
      includedSegmentIds,
      nodes,
      collectorIds,
      triggers,
      includedSegmentsIds,
    } = campaign;

    return this.apollo
      .mutate({
        mutation: ADD_WORKFLOW,
        variables: {
          input: {
            id: ulid(),
            tenantId: this.route.snapshot.queryParams['tenant'],
            name: _name ? _name : name + ' (Copy)',
            type,
            purpose,
            collectorIds,
            triggers,
            includedSegmentsIds,
          },
        },
      })
      .pipe(
        switchMap((res: any) => {
          const newCampaign = res.data.createWorkflow;
          const newCampaignId = newCampaign.id;

          return this.apollo.mutate({
            mutation: UPDATE_WORKFLOW,
            variables: {
              input: {
                id: newCampaignId,
                name: newCampaign.name,
                purpose,
                status: 'draft',
                silenced: false,
                includedSegmentIds,
                nodes: this.replaceNodeIds(nodes),
              },
            },
          });
        }),
        catchError((error) => {
          return throwError(() => new Error(error.message));
        }),
      );
  }

  replaceNodeIds(nodes: any): Node[] {
    const idMapping: { [key: string]: string } = {};

    nodes.forEach((node: any) => {
      idMapping[node.nodeId] = ulid();
    });

    return nodes.map((node: any) => {
      const newNode = { ...node, nodeId: idMapping[node.nodeId] };

      if (newNode.childrenIds) {
        newNode.childrenIds = newNode.childrenIds.map((id: string) => idMapping[id]);
      }

      if (newNode.parameters && newNode.parameters.targetNodeId) {
        newNode.parameters.targetNodeId = idMapping[newNode.parameters.targetNodeId];
      }

      if (newNode.parameters && newNode.parameters.cases) {
        if (typeof newNode.parameters.cases.true === 'string') {
          newNode.parameters.cases.true = idMapping[newNode.parameters.cases.true];
        }
        if (typeof newNode.parameters.cases.false === 'string') {
          newNode.parameters.cases.false = idMapping[newNode.parameters.cases.false];
        }
      }

      return newNode;
    });
  }

  getCampaignsDataPills(campaignId?: string): Observable<any[]> {
    return this.apollo
      .query({
        query: GET_CAMPAIGNS_LIST,
        variables: {
          criteria: {
            filters: {
              tenantId: this.route.snapshot.queryParams['tenant'],
              workflowIds: [campaignId!],
            },
          },
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res) => {
          const campaigns = (res.data as any)?.campaigns;
          return Array.isArray(campaigns) ? campaigns : [];
        }),
        catchError((error) => {
          return of([]);
        }),
      );
  }

  loadCampaignCollectorsMap(): Observable<void> {
    const tenantId = this.route.snapshot.queryParams['tenant'];

    return this.apollo
      .query({
        query: SEARCH_CAMPAIGNS_COLLECTORS_LIST,
        variables: {
          criteria: { tenantId },
        },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res: any) => res.data?.campaignCollectors ?? []),
        tap((collectors: CampaignCollector[]) => {
          // Reset the map
          this.campaignCollectorsMap.clear();

          // Group collectors by campaignId
          collectors.forEach((collector) => {
            const existing = this.campaignCollectorsMap.get(collector.campaignId) || [];
            existing.push(collector);
            this.campaignCollectorsMap.set(collector.campaignId, existing);
          });
        }),
        map(() => void 0),
        catchError((error) => {
          console.error('Error loading campaign collectors map', error);
          return of(void 0);
        }),
      );
  }

  /**
   * Get minimal collectors for a campaign
   */
  getMinimalCollectorsForCampaign(campaignId: string): CampaignCollector[] {
    return this.campaignCollectorsMap.get(campaignId) || [];
  }

  getCollectorsData(collectorIds: string[]): Observable<CampaignCollectorWithDetail[]> {
    if (!collectorIds?.length) return of([]);

    return forkJoin(
      collectorIds.map((id) =>
        this.apollo
          .query({
            query: GET_COLLECTOR,
            variables: { id },
            fetchPolicy: 'no-cache',
          })
          .pipe(
            map((res: any) => res.data?.collector),
            catchError((error) => {
              console.error('Error fetching collector:', id, error);
              return of(null);
            }),
          ),
      ),
    ).pipe(
      map((fullCollectors) =>
        fullCollectors
          .filter((c) => !!c)
          .map((c) => ({
            ...c,
            detail: {
              id: c.id,
              name: c.name,
              alias: c.alias,
              brandId: c.brandId,
              tenantId: c.tenantId,
              taxEntityId: c.taxEntityId,
              regionalConfigId: c.regionalConfigId,
              managementFeeConfig: c.managementFeeConfig,
              currency: c.currency,
              channelConfigIds: c.channelConfigIds ?? [],
              eventHookConfigIds: c.eventHookConfigIds ?? [],
              feedbackOptionIds: c.feedbackOptionIds ?? [],
              paymentGatewayIds: c.paymentGatewayIds ?? [],
              serviceIds: c.serviceIds ?? [],
            },
          })),
      ),
    );
  }
}
